// WebSocket service for online multiplayer Connect4
class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.roomCode = null;
    this.playerId = null;
    this.playerRole = null; // 'host' or 'guest'
    this.callbacks = {
      onConnected: null,
      onDisconnected: null,
      onRoomCreated: null,
      onRoomJoined: null,
      onPlayerJoined: null,
      onGameStart: null,
      onGameStarted: null,
      onMove: null,
      onGameOver: null,
      onPlayerDisconnected: null,
      onError: null
    };
  }

  // Initialize connection to WebSocket server
  connect(serverUrl = 'wss://webconnect4backend.onrender.com') {
    try {
      this.socket = new WebSocket(serverUrl);
      
      this.socket.onopen = () => {
        this.isConnected = true;
        this.playerId = this.generatePlayerId();
        console.log('Connected to game server');
        if (this.callbacks.onConnected) {
          this.callbacks.onConnected();
        }
      };

      this.socket.onmessage = (event) => {
        this.handleMessage(JSON.parse(event.data));
      };

      this.socket.onclose = () => {
        this.isConnected = false;
        console.log('Disconnected from game server');
        if (this.callbacks.onDisconnected) {
          this.callbacks.onDisconnected();
        }
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket error:', error);
        if (this.callbacks.onError) {
          this.callbacks.onError(error);
        }
      };
    } catch (error) {
      console.error('Failed to connect to server:', error);
      if (this.callbacks.onError) {
        this.callbacks.onError(error);
      }
    }
  }

  // Disconnect from server
  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.isConnected = false;
      this.roomCode = null;
      this.playerId = null;
      this.playerRole = null;
    }
  }

  // Create a new game room
  createRoom(playerName) {
    if (!this.isConnected) return false;
    
    this.send({
      type: 'CREATE_ROOM',
      playerId: this.playerId,
      playerName: playerName
    });
    
    return true;
  }

  // Join an existing game room
  joinRoom(roomCode, playerName) {
    if (!this.isConnected) return false;
    
    this.send({
      type: 'JOIN_ROOM',
      roomCode: roomCode,
      playerId: this.playerId,
      playerName: playerName
    });
    
    return true;
  }

  // Start the game (only red/host player can start)
  startGame() {
    if (!this.isConnected || (this.playerRole !== 'host' && this.playerRole !== 'red')) return false;
    
    this.send({
      type: 'START_GAME',
      roomCode: this.roomCode
    });
    
    return true;
  }

  // Send a move
  makeMove(column) {
    if (!this.isConnected || !this.roomCode) return false;
    
    this.send({
      type: 'MAKE_MOVE',
      roomCode: this.roomCode,
      playerId: this.playerId,
      column: column
    });
    
    return true;
  }

  // Leave the current room
  leaveRoom() {
    if (!this.isConnected || !this.roomCode) return false;
    
    this.send({
      type: 'LEAVE_ROOM',
      roomCode: this.roomCode,
      playerId: this.playerId
    });
    
    this.roomCode = null;
    this.playerRole = null;
    return true;
  }

  // Send message to server
  send(message) {
    if (this.socket && this.isConnected) {
      this.socket.send(JSON.stringify(message));
    }
  }

  // Handle incoming messages
  handleMessage(message) {
    switch (message.type) {
      case 'ROOM_CREATED':
        this.roomCode = message.roomCode;
        this.playerRole = 'red'; // Host is always red
        if (this.callbacks.onRoomCreated) {
          this.callbacks.onRoomCreated(message.roomCode);
        }
        break;

      case 'ROOM_JOINED':
        this.roomCode = message.roomCode;
        this.playerRole = 'yellow'; // Guest is always yellow
        if (this.callbacks.onRoomJoined) {
          this.callbacks.onRoomJoined(message.roomCode, message.players);
        }
        break;

      case 'PLAYER_JOINED':
        if (this.callbacks.onPlayerJoined) {
          this.callbacks.onPlayerJoined(message.player);
        }
        break;

      case 'GAME_STARTED':
        console.log('Game started message received:', message);
        if (this.callbacks.onGameStart) {
          this.callbacks.onGameStart(message.gameState);
        }
        // Also emit as 'gameStarted' for compatibility
        if (this.callbacks.onGameStarted) {
          this.callbacks.onGameStarted(message.gameState);
        }
        break;

      case 'MOVE_MADE':
        if (this.callbacks.onMove) {
          this.callbacks.onMove(message.move, message.gameState);
        }
        break;

      case 'GAME_OVER':
        if (this.callbacks.onGameOver) {
          this.callbacks.onGameOver(message.winner, message.gameState);
        }
        break;

      case 'PLAYER_DISCONNECTED':
        if (this.callbacks.onPlayerDisconnected) {
          this.callbacks.onPlayerDisconnected(message.playerId);
        }
        break;

      case 'ERROR':
        console.error('Server error:', message.error);
        if (this.callbacks.onError) {
          this.callbacks.onError(message.error);
        }
        break;

      default:
        console.log('Unknown message type:', message.type);
    }
  }

  // Set event callbacks
  on(event, callback) {
    if (this.callbacks.hasOwnProperty(`on${event.charAt(0).toUpperCase() + event.slice(1)}`)) {
      this.callbacks[`on${event.charAt(0).toUpperCase() + event.slice(1)}`] = callback;
    }
  }

  // Generate unique player ID
  generatePlayerId() {
    return 'player_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  }

  // Get current connection status
  getStatus() {
    return {
      isConnected: this.isConnected,
      roomCode: this.roomCode,
      playerId: this.playerId,
      playerRole: this.playerRole
    };
  }
}

export default WebSocketService;
